import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IServerSideGetRowsParams, IServerSideGetRowsRequest, SortModelItem } from "ag-grid-community";
import { omit } from "lodash";

import { DEFAULT_PAGE_LIMIT } from "fond/constants";
import { getAttachments } from "fond/redux/attachments";
import { AppThunkDispatch, Attachment, AttachmentEntityType, AttachmentFilterType, AttachmentStateMetadata, BasePagination, Store } from "fond/types";
import { pageNumberForPaginationSet } from "fond/utils/pagination";

type PageFromCacheParams = {
  entityId: string;
  meta?: AttachmentStateMetadata;
  pagination: Required<BasePagination>;
  items: Attachment[] | null;
  sortModel?: SortModelItem[];
  filterType?: AttachmentFilterType;
  versionIds?: Array<string | null>;
};

/**
 * Determine if the request is for the same contiguous dataset, and if this request can be returned from the cache.
 * A request is considered the same if:
 * - The entity id is the same
 * - The pagination limit is the same (different limits will cause different sets of cached pages)
 * - The sort model is the same
 * - The page has already been fetched
 * - The items are not null
 */
const pageFromCache = ({ entityId, meta, pagination, items, sortModel, filterType, versionIds }: PageFromCacheParams) => {
  if (!meta || !items) {
    return null;
  }
  const isSameEntity = meta.entityId === entityId;
  const isSameFilterType = meta.filterType === filterType;
  const isSamePaginationLimit = meta.limit === pagination.Limit;
  const hasPage = meta.fetchedPages?.includes(pageNumberForPaginationSet(pagination));
  const isSameSortModel = JSON.stringify(meta.sortModel) === JSON.stringify(sortModel);
  const isSameVersionIds = JSON.stringify(meta.versionIds) === JSON.stringify(versionIds);

  if (isSameEntity && hasPage && isSameSortModel && isSamePaginationLimit && isSameFilterType && isSameVersionIds) {
    return items.slice(pagination.Start, pagination.Start + pagination.Limit);
  }
  return null;
};

export type GetRowsParams = {
  request: Partial<IServerSideGetRowsRequest>;
  success: IServerSideGetRowsParams["success"];
  fail: IServerSideGetRowsParams["fail"];
};

export type UseGetAttachmentRows = [(params: GetRowsParams) => Promise<void>, Attachment[] | null, number];
export const useGetAttachmentRows = (projectId: string, featureId?: string | null, filterType?: AttachmentFilterType): UseGetAttachmentRows => {
  const attachmentEntityType: AttachmentEntityType = featureId ? "Feature" : "Project";
  const dispatch: AppThunkDispatch = useDispatch();
  const items = useSelector((state: Store) => state.attachments.items[attachmentEntityType]);
  const meta = useSelector((state: Store) => state.attachments.meta[attachmentEntityType]);

  const metaRef = useRef<AttachmentStateMetadata | undefined>(undefined);
  const itemsRef = useRef<Attachment[] | null>(null);

  useEffect(() => {
    metaRef.current = meta ?? undefined;
  }, [meta]);

  useEffect(() => {
    itemsRef.current = items ?? null;
  }, [items]);

  const getRowsInternal = useCallback(
    async (params: GetRowsParams) => {
      const Start = params.request.startRow ?? 0;
      const Limit = params.request.endRow ? params.request.endRow - Start : DEFAULT_PAGE_LIMIT;
      const { sortModel, filterModel } = params.request;
      const versionFilterValues = (filterModel as Record<string, { values?: Array<string | null> }> | undefined)?.["Version.ID"]?.values;
      const versionIds: Array<string | null> | undefined = versionFilterValues?.length ? versionFilterValues : undefined;
      const pagination = {
        Start,
        Limit,
      };

      // Note: AgGrid v35 may include a 'type' field in the sort model (e.g. 'text', 'number'),
      // but our backend and caching logic currently only support sorting by field and direction.
      // To keep the request and cache keys compatible with the existing API, we explicitly drop
      // the 'type' field here. If backend support for sortModel.type is added in the future,
      // this sanitisation should be revisited to pass the field through end-to-end.
      const sortModelWithoutType = sortModel?.map((item) => omit(item, "type"));

      try {
        // check if the attachment data is already in the store
        const cachedPage = pageFromCache({
          entityId: featureId ?? projectId,
          meta: metaRef.current,
          pagination,
          items: itemsRef.current,
          sortModel: sortModelWithoutType,
          filterType,
          versionIds,
        });
        if (cachedPage) {
          params.success({
            rowData: cachedPage,
            rowCount: metaRef.current?.total ?? 0,
          });
          return;
        }
        const res = await dispatch(
          getAttachments({
            projectId,
            layerFeatureId: featureId,
            pagination,
            sortModel: sortModelWithoutType,
            filterType: featureId ? null : filterType,
            versionIds,
          })
        );
        if (!res?.Attachments) {
          throw new Error("Failed to fetch attachment data");
        }

        params.success({
          rowData: res.Attachments,
          rowCount: res.Pagination.Total,
        });
      } catch (error) {
        console.error("Failed to fetch attachment data", error);
        params.fail();
      }
    },
    [dispatch, featureId, projectId, filterType]
  );

  // Use a ref to store the internal reference to the getRows function
  // to ensure that the latest version of the function is always used
  // This is important because the getRows function is called by AgGrid
  // which has its own memoization that closes over the function
  const getRowsRef = useRef<{ getRowsInternal: (params: GetRowsParams) => Promise<void> }>({ getRowsInternal });

  useEffect(() => {
    getRowsRef.current!.getRowsInternal = getRowsInternal;
  }, [getRowsInternal, filterType, meta]);

  return [getRowsRef.current.getRowsInternal, items, meta?.total ?? 0];
};
